(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-welltayl-system-pdp]";
  const FINAL_SELECTOR = "[data-welltayl-system-final]";
  const routesRoot = window.Shopify?.routes?.root || "/";

  class WelltaylSystemPdp {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.products = this.config.products || [];
      this.packageIndex = Math.max(0, Number(this.config.defaultPackage || 2) - 1);
      this.variantIndex = 0;
      this.cart = null;
      this.unlockedGifts = new Set();
      this.form = root.querySelector(".welltayl-system-form");
      this.status = root.querySelector("[data-welltayl-form-status]");
      this.money = new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: this.config.currency || "USD"
      });
    }

    readConfig() {
      try {
        return JSON.parse(this.root.querySelector("[data-welltayl-system-config]")?.textContent || "{}");
      } catch (error) {
        console.error("Welltayl System: invalid configuration", error);
        return {};
      }
    }

    init() {
      if (this.root.welltaylSystemController || !this.form) return;
      this.root.welltaylSystemController = this;

      this.root.querySelectorAll("[data-welltayl-package-card] input").forEach((radio) => {
        radio.addEventListener("change", () => this.selectPackage(Number(radio.value) - 1, true));
      });

      this.root.querySelector("[data-welltayl-variant-select]")?.addEventListener("change", (event) => {
        const product = this.currentProduct;
        this.variantIndex = Math.max(0, product.variants.findIndex((variant) => String(variant.id) === event.target.value));
        this.renderSelection({ preserveGallery: true });
      });

      this.form.addEventListener("submit", (event) => this.onSubmit(event));
      this.root.querySelector("[data-welltayl-sticky-add]")?.addEventListener("click", () => this.form.requestSubmit());
      this.root.querySelectorAll("[data-welltayl-post-add-close]").forEach((button) => {
        button.addEventListener("click", () => this.closePostAdd());
      });
      this.root.querySelector("[data-welltayl-upsell-dismiss]")?.addEventListener("click", () => this.closePostAdd());
      this.root.querySelector("[data-welltayl-upsell-add]")?.addEventListener("click", () => this.addUpsell());
      this.root.querySelector("[data-welltayl-upgrade]")?.addEventListener("click", (event) => {
        const target = Number(event.currentTarget.dataset.targetPackage);
        if (target > 0) this.selectPackage(target - 1, true);
      });
      this.root.querySelector("[data-welltayl-order-bump] input")?.addEventListener("change", (event) => {
        const card = event.currentTarget.closest("[data-welltayl-order-bump]");
        card?.classList.toggle("is-selected", event.currentTarget.checked);
        const action = card?.querySelector("[data-welltayl-bump-label]");
        if (action) action.textContent = event.currentTarget.checked ? "Added ✓" : "+ Add";
      });

      this.fetchCartOnce();
      this.selectPackage(this.packageIndex, false);
      this.observeMainButton();
      this.bindFinalSelector();
    }

    get currentProduct() {
      return this.products[this.packageIndex] || { variants: [], media: [] };
    }

    get currentVariant() {
      return this.currentProduct.variants?.[this.variantIndex] || this.currentProduct.variants?.[0] || null;
    }

    get galleryMedia() {
      const mediaGroups = [];
      const seen = new Set();

      this.products.forEach((product, packageIndex) => {
        let mediaItems = [...(product.media || [])];
        if (mediaItems.length === 0 && product.fallbackImage?.src) {
          mediaItems = [{
            id: `fallback-${packageIndex}`,
            type: "image",
            alt: product.fallbackImage.alt || product.displayName,
            preview: product.fallbackImage
          }];
        }

        mediaGroups[packageIndex] = mediaItems.map((media) => {
          const key = `${product.id}-${media.id || media.preview?.src}`;
          if (seen.has(key)) return null;
          seen.add(key);
          return { ...media, packageIndex, productName: product.displayName };
        }).filter(Boolean);
      });

      const ordered = [];
      const conversionSequence = [
        [1, 0],
        [2, 0],
        [0, 0],
        [2, 1],
        [2, 5],
        [2, 4],
        [2, 6],
        [2, 3]
      ];

      conversionSequence.forEach(([packageIndex, mediaIndex]) => {
        const media = mediaGroups[packageIndex]?.[mediaIndex];
        if (!media) return;
        ordered.push(media);
      });

      const gallery = ordered.length > 0 ? ordered : mediaGroups.flat();

      if (this.config.leadImage?.src) {
        gallery.unshift({
          id: "lead-image",
          type: "image",
          alt: this.config.leadImage.alt || "",
          preview: {
            src: this.config.leadImage.src,
            srcset: this.config.leadImage.src,
            width: 1024,
            height: 1024
          },
          packageIndex: -1
        });
      }

      return gallery;
    }

    format(cents) {
      return this.money.format((Number(cents) || 0) / 100);
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }

    eventDetail(extra = {}) {
      const product = this.currentProduct;
      const variant = this.currentVariant;
      return {
        productHandle: product.handle,
        productId: product.id,
        variantId: variant?.id,
        packageLabel: product.displayName,
        price: variant?.price,
        ...extra
      };
    }

    selectPackage(index, emitEvent = true) {
      if (!this.products[index]?.id) return;
      this.packageIndex = index;
      const selectedId = Number(this.products[index].selectedVariantId);
      this.variantIndex = Math.max(0, this.products[index].variants.findIndex((variant) => Number(variant.id) === selectedId));

      this.root.querySelectorAll("[data-welltayl-package-card]").forEach((card, cardIndex) => {
        const selected = cardIndex === index;
        card.classList.toggle("is-selected", selected);
        const input = card.querySelector("input");
        if (input) input.checked = selected;
      });

      this.renderSelection();
      if (emitEvent) this.emit("welltayl:package_selected", this.eventDetail());
    }

    renderSelection({ preserveGallery = false } = {}) {
      const product = this.currentProduct;
      const variant = this.currentVariant;
      if (!variant) return;

      this.setText("[data-welltayl-selected-name]", product.displayName || product.title);
      this.setText("[data-welltayl-selected-price]", this.format(variant.price));
      this.setText("[data-welltayl-add-price]", this.format(this.purchasePrice));
      this.setText("[data-welltayl-sticky-name]", product.displayName || product.title);
      this.setText("[data-welltayl-sticky-price]", this.format(this.purchasePrice));

      const compare = this.root.querySelector("[data-welltayl-selected-compare]");
      if (compare) {
        compare.hidden = !(variant.compareAtPrice > variant.price);
        compare.textContent = this.format(variant.compareAtPrice);
      }

      const variantInput = this.form.querySelector("[data-welltayl-variant-id]");
      if (variantInput) variantInput.value = variant.id;

      this.renderVariantPicker();
      this.renderSelectedDetails();
      this.renderCurrentPackageCard();
      this.renderSubscriptions();
      this.renderUpgrade();
      this.renderOrderBump();
      this.renderButton();
      this.renderGiftProgress();
      if (!preserveGallery) this.renderGallery();
      this.syncFinalSelector();
    }

    get purchasePrice() {
      const selectedPlan = this.selectedPlan;
      return selectedPlan?.price || this.currentVariant?.price || 0;
    }

    get selectedPlan() {
      const planId = Number(this.form.querySelector("[data-welltayl-selling-plan]")?.value || 0);
      return this.currentVariant?.sellingPlans?.find((plan) => Number(plan.id) === planId);
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || "";
      });
    }

    renderSelectedDetails() {
      const product = this.currentProduct;
      const contents = this.root.querySelector("[data-welltayl-selected-contents]");
      const description = this.root.querySelector("[data-welltayl-selected-description]");
      if (description) description.textContent = product.description || "";
      if (!contents) return;

      contents.replaceChildren();
      String(product.contents || "")
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
          const row = document.createElement("span");
          const check = document.createElement("b");
          const text = document.createElement("span");
          check.textContent = "✓";
          text.textContent = item;
          row.append(check, text);

          const isCompleteGift = this.packageIndex === 2 && /calming lick mat|travel pouch/i.test(item);
          if (isCompleteGift) {
            const free = document.createElement("em");
            free.textContent = "FREE";
            row.append(free);
          }
          contents.append(row);
        });
    }

    renderCurrentPackageCard() {
      const card = this.root.querySelector(`[data-welltayl-package-card][data-package-index="${this.packageIndex + 1}"]`);
      const variant = this.currentVariant;
      if (!card || !variant) return;
      const reference = Math.max(Number(variant.compareAtPrice || 0), Number(this.currentProduct.individualValue || 0));
      const savings = Math.max(0, reference - variant.price);
      const price = card.querySelector("[data-package-card-price]");
      const referenceElement = card.querySelector("[data-package-card-reference]");
      const savingsElement = card.querySelector("[data-package-card-savings]");
      if (price) price.textContent = this.format(variant.price);
      if (referenceElement) {
        referenceElement.hidden = reference <= variant.price;
        referenceElement.textContent = reference > variant.price ? this.format(reference) : "";
      }
      if (savingsElement) {
        savingsElement.hidden = savings <= 0;
        savingsElement.textContent = savings > 0 ? `Save ${this.format(savings)}` : "";
      }
    }

    renderVariantPicker() {
      const wrapper = this.root.querySelector("[data-welltayl-variant-picker]");
      const select = this.root.querySelector("[data-welltayl-variant-select]");
      if (!wrapper || !select) return;

      const variants = this.currentProduct.variants || [];
      wrapper.hidden = variants.length <= 1;
      select.replaceChildren();

      variants.forEach((variant) => {
        const option = new Option(
          `${variant.title}${variant.available ? "" : " — Sold out"}`,
          variant.id,
          false,
          Number(variant.id) === Number(this.currentVariant?.id)
        );
        option.disabled = !variant.available;
        select.add(option);
      });
    }

    renderSubscriptions() {
      const fieldset = this.root.querySelector("[data-welltayl-subscriptions]");
      const plansContainer = this.root.querySelector("[data-welltayl-selling-plans]");
      const hiddenInput = this.root.querySelector("[data-welltayl-selling-plan]");
      if (!fieldset || !plansContainer || !hiddenInput) return;

      const plans = this.currentVariant?.sellingPlans || [];
      fieldset.hidden = false;
      hiddenInput.value = "";

      const oneTime = fieldset.querySelector("[data-welltayl-one-time]");
      const subscribe = fieldset.querySelector("[data-welltayl-subscribe]");
      const frequency = fieldset.querySelector("[data-welltayl-frequency]");
      const oneTimeCard = fieldset.querySelector('[data-welltayl-purchase-card="one-time"]');
      const subscribeCard = fieldset.querySelector('[data-welltayl-purchase-card="subscribe"]');
      plansContainer.replaceChildren();

      if (plans.length === 0) {
        if (oneTime) oneTime.checked = true;
        if (subscribe) subscribe.checked = false;
        oneTimeCard?.classList.add("is-selected");
        if (subscribeCard) subscribeCard.hidden = true;
        if (frequency) frequency.hidden = true;
        this.setText("[data-welltayl-one-time-price]", this.format(this.currentVariant.price));
        return;
      }

      if (subscribeCard) subscribeCard.hidden = false;
      const firstPlan = plans[0];
      const firstSavings = Math.max(0, this.currentVariant.price - firstPlan.price);
      const firstPercent = this.currentVariant.price > 0 ? Math.round((firstSavings / this.currentVariant.price) * 100) : 0;

      if (oneTime) oneTime.checked = true;
      if (subscribe) subscribe.checked = false;
      oneTimeCard?.classList.add("is-selected");
      subscribeCard?.classList.remove("is-selected");
      if (frequency) frequency.hidden = true;
      this.setText("[data-welltayl-one-time-price]", this.format(this.currentVariant.price));
      this.setText("[data-welltayl-subscribe-title]", firstPercent > 0 ? `Subscribe & Save ${firstPercent}%` : "Subscribe & Save");
      this.setText("[data-welltayl-subscribe-price]", this.format(firstPlan.price));
      this.setText("[data-welltayl-subscribe-savings]", firstSavings > 0 ? `Save ${this.format(firstSavings)}` : "");
      const selectPlan = (plan) => {
        hiddenInput.value = plan.id;
        const savings = Math.max(0, this.currentVariant.price - plan.price);
        const percent = this.currentVariant.price > 0 ? Math.round((savings / this.currentVariant.price) * 100) : 0;
        this.setText("[data-welltayl-subscribe-title]", percent > 0 ? `Subscribe & Save ${percent}%` : "Subscribe & Save");
        this.setText("[data-welltayl-subscribe-price]", this.format(plan.price));
        this.setText("[data-welltayl-subscribe-savings]", savings > 0 ? `Save ${this.format(savings)}` : "");
        this.renderButton();
        this.renderGiftProgress();
        this.emit("welltayl:subscription_selected", this.eventDetail({ sellingPlanId: plan.id }));
      };

      plans.forEach((plan) => {
        const label = document.createElement("label");
        const radio = document.createElement("input");
        const text = document.createElement("span");
        label.className = "welltayl-system-frequency__pill";
        radio.type = "radio";
        radio.name = `welltayl-system-frequency-${this.config.sectionId}`;
        radio.value = plan.id;
        radio.className = "welltayl-system-choice-input";
        text.textContent = plan.name;
        radio.addEventListener("change", () => {
          plansContainer.querySelectorAll("label").forEach((item) => item.classList.toggle("is-selected", item === label));
          selectPlan(plan);
        });
        label.append(radio, text);
        plansContainer.append(label);
      });

      oneTime.onchange = () => {
        hiddenInput.value = "";
        oneTimeCard?.classList.add("is-selected");
        subscribeCard?.classList.remove("is-selected");
        if (frequency) frequency.hidden = true;
        this.renderButton();
        this.renderGiftProgress();
        this.emit("welltayl:subscription_selected", this.eventDetail({ sellingPlanId: null }));
      };

      subscribe.onchange = () => {
        oneTimeCard?.classList.remove("is-selected");
        subscribeCard?.classList.add("is-selected");
        if (frequency) frequency.hidden = false;
        const firstRadio = plansContainer.querySelector("input");
        if (firstRadio) {
          firstRadio.checked = true;
          firstRadio.closest("label")?.classList.add("is-selected");
        }
        selectPlan(firstPlan);
      };
    }

    renderUpgrade() {
      const upgrade = this.root.querySelector("[data-welltayl-upgrade]");
      const message = this.root.querySelector("[data-welltayl-upgrade-message]");
      const items = this.root.querySelector("[data-welltayl-upgrade-items]");
      if (!upgrade || !message || !items) return;

      const currentPrice = this.currentVariant?.price || 0;
      const nextProduct = this.products[this.packageIndex + 1];
      const nextPrice = nextProduct?.variants?.[0]?.price || 0;
      upgrade.hidden = false;
      upgrade.disabled = false;

      if (this.packageIndex === 0 && nextPrice > currentPrice) {
        message.textContent = `Get the complete 3-step system for just ${this.format(nextPrice - currentPrice)} more`;
        items.textContent = "+ Skin + Coat Spray  •  + Skin Barrier Stick";
        upgrade.dataset.targetPackage = "2";
      } else if (this.packageIndex === 1 && nextPrice > currentPrice) {
        message.textContent = `Upgrade to Complete for just ${this.format(nextPrice - currentPrice)} more`;
        items.textContent = "+ Bath Brush  •  + Calming Lick Mat  •  + Travel Pouch";
        upgrade.dataset.targetPackage = "3";
      } else if (this.packageIndex === 2) {
        message.textContent = "You're getting the complete routine + best value.";
        items.textContent = "Calming Lick Mat + Travel Pouch included FREE";
        upgrade.removeAttribute("data-target-package");
        upgrade.disabled = true;
      } else {
        upgrade.hidden = true;
      }
    }

    renderOrderBump() {
      const bump = this.root.querySelector("[data-welltayl-order-bump]");
      if (!bump) return;
      const sameProduct = Number(this.config.orderBump?.productId) === Number(this.currentProduct.id);
      bump.hidden = sameProduct || (this.config.hideBumpOnComplete && this.packageIndex === 2);
      if (bump.hidden) {
        const checkbox = bump.querySelector("input");
        if (checkbox) checkbox.checked = false;
        bump.classList.remove("is-selected");
      }
    }

    renderButton() {
      const variant = this.currentVariant;
      const button = this.root.querySelector("[data-welltayl-add-button]");
      if (!button || !variant) return;

      button.disabled = !variant.available;
      const label = "ADD TO CART";
      this.setText("[data-welltayl-add-label]", label);
      this.setText("[data-welltayl-add-price]", this.format(this.purchasePrice));
      this.setText("[data-welltayl-sticky-price]", this.format(this.purchasePrice));

      const stickyButton = this.root.querySelector("[data-welltayl-sticky-add]");
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = label;
      }

      const shipping = this.root.querySelector("[data-welltayl-shipping-reassurance]");
      if (shipping) {
        const threshold = Number(this.root.querySelector("[data-welltayl-gifts]")?.dataset.shippingThreshold || 0);
        shipping.hidden = threshold > 0 && (this.cart?.total_price || 0) + this.purchasePrice < threshold;
      }
    }

    renderGallery() {
      const stage = this.root.querySelector("[data-welltayl-gallery-stage]");
      const thumbnails = this.root.querySelector("[data-welltayl-gallery-thumbnails]");
      if (!stage || !thumbnails) return;

      const media = this.galleryMedia;
      const mobileSlider = window.matchMedia("(max-width: 699px)").matches;
      // First render leads with the hero image; later renders (package switches) jump to that package's media.
      const selectedMediaIndex = !this.galleryInitialized && this.config.leadImage?.src
        ? 0
        : Math.max(0, media.findIndex((item) => item.packageIndex === this.packageIndex));
      this.galleryInitialized = true;

      stage.replaceChildren();
      thumbnails.replaceChildren();
      media.forEach((item, index) => {
        const button = document.createElement("button");
        const image = document.createElement("img");
        button.type = "button";
        button.className = "welltayl-system-gallery__thumbnail";
        button.setAttribute("aria-label", `View ${item.productName || "product"} media ${index + 1}`);
        button.setAttribute("aria-current", String(index === selectedMediaIndex));
        image.src = item.preview?.src || "";
        image.alt = "";
        image.loading = "lazy";
        button.append(image);
        button.addEventListener("click", () => {
          if (mobileSlider) {
            stage.scrollTo({
              left: index * stage.clientWidth,
              behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
            });
          } else {
            this.renderActiveMedia(item, index === selectedMediaIndex);
          }
          thumbnails.querySelectorAll("button").forEach((thumb) => thumb.setAttribute("aria-current", String(thumb === button)));
        });
        thumbnails.append(button);

        if (mobileSlider) {
          const slide = document.createElement("div");
          slide.className = "welltayl-system-gallery__slide";
          slide.dataset.mediaIndex = index;
          slide.append(this.createMediaElement(item, index === selectedMediaIndex));
          stage.append(slide);
        }
      });

      if (mobileSlider) {
        stage.scrollLeft = selectedMediaIndex * stage.clientWidth;
        let scrollFrame;
        stage.onscroll = () => {
          cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            const index = Math.max(0, Math.min(media.length - 1, Math.round(stage.scrollLeft / Math.max(stage.clientWidth, 1))));
            thumbnails.querySelectorAll("button").forEach((thumb, thumbIndex) => {
              thumb.setAttribute("aria-current", String(thumbIndex === index));
            });
          });
        };
      } else if (media[selectedMediaIndex]) {
        this.renderActiveMedia(media[selectedMediaIndex], true);
      }
      thumbnails.hidden = media.length <= 1;
    }

    createMediaElement(media, eager = false) {
      if (media.type === "video" && media.sources?.length) {
        const video = document.createElement("video");
        video.className = "welltayl-system-gallery__video";
        video.controls = true;
        video.playsInline = true;
        video.poster = media.preview?.src || "";
        media.sources.forEach((videoSource) => {
          const source = document.createElement("source");
          source.src = videoSource.url;
          source.type = videoSource.mimeType;
          video.append(source);
        });
        return video;
      }

      const image = document.createElement("img");
      image.className = "welltayl-system-gallery__image";
      image.src = media.preview?.src || "";
      if (media.preview?.srcset) image.srcset = media.preview.srcset;
      image.sizes = "(max-width: 999px) 100vw, 52vw";
      image.alt = media.alt || this.currentProduct.displayName || "";
      image.loading = eager ? "eager" : "lazy";
      image.decoding = "async";
      return image;
    }

    renderActiveMedia(media, eager = false) {
      const stage = this.root.querySelector("[data-welltayl-gallery-stage]");
      if (!stage) return;
      stage.replaceChildren(this.createMediaElement(media, eager));
    }

    async fetchCartOnce() {
      if (this.cart) return this.cart;
      try {
        this.cart = await fetch(`${routesRoot}cart.js`).then((response) => response.json());
        this.renderGiftProgress();
      } catch {
        this.cart = { total_price: 0, items: [] };
      }
      return this.cart;
    }

    renderGiftProgress() {
      const gifts = this.root.querySelector("[data-welltayl-gifts]");
      if (!gifts) return;

      const selectedTotal = this.purchasePrice;
      const pouch = Number(gifts.dataset.pouchThreshold || 0);
      const lick = Number(gifts.dataset.lickThreshold || 0);
      const max = Math.max(pouch, lick, 1);
      const progress = Math.min(100, Math.round((selectedTotal / max) * 100));
      const progressBar = gifts.querySelector("[data-welltayl-gift-progress]");
      const message = gifts.querySelector("[data-welltayl-gift-message]");
      if (progressBar) progressBar.style.width = `${progress}%`;

      const states = {
        pouch: {
          threshold: pouch,
          unlocked: pouch > 0 && selectedTotal >= pouch
        },
        lickMat: {
          threshold: lick,
          unlocked: lick > 0 && selectedTotal >= lick
        }
      };

      Object.entries(states).forEach(([key, state]) => {
        const gift = gifts.querySelector(`[data-welltayl-gift="${key}"]`);
        if (!gift) return;
        gift.classList.toggle("is-unlocked", state.unlocked);
        const pill = gift.querySelector("[data-welltayl-gift-pill]");
        const status = gift.querySelector("[data-welltayl-gift-state]");
        const free = gift.querySelector("[data-welltayl-gift-free]");
        if (pill) pill.textContent = state.unlocked ? "FREE" : "LOCKED";
        if (free) free.hidden = !state.unlocked;
        if (status) {
          status.textContent = state.unlocked ? "✓ Unlocked with your routine" : "Locked";
        }

        const marker = gifts.querySelector(`[data-welltayl-gift-marker="${key}"]`);
        const thresholdLabel = gifts.querySelector(`[data-welltayl-gift-threshold="${key}"]`);
        if (marker) {
          marker.style.left = `${Math.min(100, (state.threshold / max) * 100)}%`;
          marker.classList.toggle("is-unlocked", state.unlocked);
        }
        if (thresholdLabel) thresholdLabel.textContent = this.format(state.threshold);
      });

      let tier = "shipping";
      let copy;
      const giftsHeading = gifts.querySelector("[data-welltayl-gifts-heading]");
      if (giftsHeading) {
        giftsHeading.textContent = states.pouch.unlocked || states.lickMat.unlocked
          ? "FREE Gifts Unlocked"
          : "Unlock FREE Gifts";
      }

      if (states.lickMat.unlocked && states.pouch.unlocked) {
        tier = "all";
        copy = "You've unlocked both free gifts.";
      } else if (states.pouch.unlocked) {
        tier = "pouch";
        copy = `Free pouch unlocked! You're only ${this.format(Math.max(0, lick - selectedTotal))} away from your free Calming Lick Mat.`;
      } else {
        copy = `You're ${this.format(Math.max(0, pouch - selectedTotal))} away from your first free gift.`;
      }
      if (message) message.textContent = copy;

      if (!this.unlockedGifts.has(tier) && ["pouch", "all"].includes(tier)) {
        this.unlockedGifts.add(tier);
        this.emit("welltayl:gift_unlocked", this.eventDetail({ tier }));
      }
    }

    automaticGiftItems() {
      if (!this.config.enableAutomaticGifts) return [];
      const projectedTotal = this.purchasePrice;
      const items = [];
      const existing = this.cart?.items || [];

      Object.entries(this.config.gifts || {}).forEach(([tier, gift]) => {
        if (!gift.variantId || projectedTotal < Number(gift.threshold || 0)) return;
        // Any existing line with this gift variant counts — including gifts added by the
        // theme's metafield mechanism (_is_gift) — so the two systems never double-add.
        const alreadyAdded = existing.some((item) => Number(item.variant_id) === Number(gift.variantId));
        if (!alreadyAdded) {
          items.push({
            id: gift.variantId,
            quantity: 1,
            properties: {
              _welltayl_auto_gift: "true",
              _welltayl_gift_tier: tier
            }
          });
        }
      });
      return items;
    }

    async cleanupDuplicateGifts() {
      const giftVariantIds = new Set(
        Object.values(this.config.gifts || {}).map((gift) => Number(gift.variantId)).filter(Boolean)
      );
      if (!giftVariantIds.size) return;

      const seen = new Set();
      const changes = [];
      (this.cart?.items || []).forEach((item) => {
        const variantId = Number(item.variant_id);
        if (!giftVariantIds.has(variantId)) return;
        if (seen.has(variantId)) {
          changes.push({ id: item.key, quantity: 0 });
        } else {
          seen.add(variantId);
          if (item.quantity > 1) changes.push({ id: item.key, quantity: 1 });
        }
      });
      if (!changes.length) return;

      try {
        for (const change of changes) {
          await fetch(`${routesRoot}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(change)
          });
        }
        this.cart = null;
        await this.fetchCartOnce();
      } catch {
        // Cleanup is best-effort; never block the purchase.
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      const variant = this.currentVariant;
      if (!variant?.available) return;

      const button = this.root.querySelector("[data-welltayl-add-button]");
      button?.setAttribute("aria-busy", "true");
      if (this.status) this.status.textContent = "Adding your routine…";

      this.cart = null;
      await this.fetchCartOnce();
      await this.cleanupDuplicateGifts();
      const mainItem = { id: variant.id, quantity: 1 };
      const sellingPlanId = Number(this.form.querySelector("[data-welltayl-selling-plan]")?.value || 0);
      if (sellingPlanId) mainItem.selling_plan = sellingPlanId;

      const items = [mainItem];
      const bump = this.root.querySelector("[data-welltayl-order-bump] input:checked");
      if (bump && !bump.closest("[data-welltayl-order-bump]").hidden) {
        items.push({ id: Number(bump.value), quantity: 1, properties: { _welltayl_order_bump: "true" } });
      }
      items.push(...this.automaticGiftItems());

      try {
        let result;
        try {
          result = await this.addItems(items);
        } catch (error) {
          if (items.length === 1) throw error;
          result = await this.addItems([mainItem]);
        }
        await this.notifyThemeCart(result);
        this.emit("welltayl:add_to_cart", this.eventDetail({ sellingPlanId: sellingPlanId || null }));
        if (this.status) this.status.textContent = "";
        this.openPostAdd();
      } catch (error) {
        if (this.status) this.status.textContent = error.message || "This item could not be added. Please try again.";
      } finally {
        button?.removeAttribute("aria-busy");
      }
    }

    async addItems(items) {
      const sections = new Set();
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections }
      }));

      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items, sections: [...sections].join(",") })
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || "Unable to add item");
      return json;
    }

    async notifyThemeCart(addResponse) {
      this.cart = await fetch(`${routesRoot}cart.js`).then((response) => response.json());
      this.cart.sections = addResponse.sections || {};
      const items = addResponse.items || [addResponse];

      this.form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: { items, cart: this.cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "variant:add", cart: this.cart }
      }));
      this.renderGiftProgress();
    }

    openPostAdd() {
      const panel = this.root.querySelector("[data-welltayl-post-add]");
      if (!panel) return;
      panel.hidden = false;
      const upsell = this.config.upsells?.[this.packageIndex];
      const content = panel.querySelector("[data-welltayl-upsell-content]");
      const canUpsell = this.config.enablePostAddUpsell && upsell?.available && upsell.variantId;
      if (content) content.hidden = !canUpsell;
      if (canUpsell) {
        this.setText("[data-welltayl-upsell-name]", upsell.title);
        this.setText("[data-welltayl-upsell-price]", this.format(upsell.price));
        this.emit("welltayl:upsell_viewed", this.eventDetail({ upsellVariantId: upsell.variantId }));
      }
      panel.querySelector("[data-welltayl-post-add-close]")?.focus();
    }

    closePostAdd() {
      const panel = this.root.querySelector("[data-welltayl-post-add]");
      if (panel) panel.hidden = true;
    }

    async addUpsell() {
      const upsell = this.config.upsells?.[this.packageIndex];
      if (!upsell?.variantId) return;
      const button = this.root.querySelector("[data-welltayl-upsell-add]");
      button?.setAttribute("aria-busy", "true");
      try {
        const result = await this.addItems([{ id: upsell.variantId, quantity: 1, properties: { _welltayl_upsell: "true" } }]);
        await this.notifyThemeCart(result);
        this.emit("welltayl:upsell_added", this.eventDetail({ upsellVariantId: upsell.variantId }));
        this.closePostAdd();
      } finally {
        button?.removeAttribute("aria-busy");
      }
    }

    observeMainButton() {
      const button = this.root.querySelector("[data-welltayl-add-button]");
      const sticky = this.root.querySelector("[data-welltayl-sticky]");
      if (!button || !sticky || !("IntersectionObserver" in window)) return;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting;
      }, { threshold: 0.1 });
      this.stickyObserver.observe(button);
    }

    bindFinalSelector() {
      document.querySelectorAll(FINAL_SELECTOR).forEach((finalSection) => {
        if (finalSection.welltaylSystemBound) return;
        finalSection.welltaylSystemBound = true;
        finalSection.querySelectorAll("[data-final-package]").forEach((button) => {
          button.addEventListener("click", () => {
            this.selectPackage(Number(button.dataset.finalPackage) - 1, true);
          });
        });
        finalSection.querySelector("[data-final-add]")?.addEventListener("click", () => this.form.requestSubmit());
      });
      this.syncFinalSelector();
    }

    syncFinalSelector() {
      document.querySelectorAll(FINAL_SELECTOR).forEach((finalSection) => {
        finalSection.querySelectorAll("[data-final-package]").forEach((button, index) => {
          const product = this.products[index];
          const variant = product?.variants?.[0];
          button.hidden = !product?.id;
          button.setAttribute("aria-pressed", String(index === this.packageIndex));
          const name = button.querySelector("[data-final-name]");
          const price = button.querySelector("[data-final-price]");
          if (name) name.textContent = product?.displayName || "";
          if (price) price.textContent = this.format(variant?.price || 0);
        });
        const selected = finalSection.querySelector("[data-final-selected]");
        if (selected) selected.textContent = `${this.currentProduct.displayName} — ${this.format(this.purchasePrice)}`;
      });
    }

    destroy() {
      this.stickyObserver?.disconnect();
      delete this.root.welltaylSystemController;
    }
  }

  function initPdp(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new WelltaylSystemPdp(root).init());
    initFaq(scope);
    document.querySelector(ROOT_SELECTOR)?.welltaylSystemController?.bindFinalSelector();
  }

  function initFaq(scope = document) {
    scope.querySelectorAll("[data-welltayl-system-faq]").forEach((faq) => {
      if (faq.welltaylSystemBound) return;
      faq.welltaylSystemBound = true;
      faq.querySelectorAll("[data-welltayl-faq-button]").forEach((button) => {
        button.addEventListener("click", () => {
          const expanded = button.getAttribute("aria-expanded") === "true";
          const panel = document.getElementById(button.getAttribute("aria-controls"));
          button.setAttribute("aria-expanded", String(!expanded));
          if (panel) panel.hidden = expanded;
        });
      });
    });
  }

  if (!window.WelltaylSystemPdp) {
    window.WelltaylSystemPdp = { init: initPdp };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => initPdp());
    } else {
      initPdp();
    }

    document.addEventListener("shopify:section:load", (event) => initPdp(event.target));
    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelector(ROOT_SELECTOR)?.welltaylSystemController?.destroy();
    });
  } else {
    window.WelltaylSystemPdp.init();
  }
})();
